import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Real-time WebSocket hook.
 * - Uses cookie-based auth (access_token is sent automatically)
 * - Auto-reconnects with exponential backoff
 * - Calls onMessage for every JSON message received
 *
 * Returns { connected, lastMessage, send, close }
 */
export default function useWebSocket(onMessage) {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const retriesRef = useRef(0);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  const connect = useCallback(() => {
    const httpBase = process.env.REACT_APP_BACKEND_URL;
    if (!httpBase) return;
    const wsUrl = httpBase.replace(/^http/, 'ws') + '/api/ws';

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      retriesRef.current = 0;
    };
    ws.onmessage = (ev) => {
      if (ev.data === 'pong') return;
      try {
        const msg = JSON.parse(ev.data);
        setLastMessage(msg);
        if (handlerRef.current) handlerRef.current(msg);
      } catch {
        // ignore non-JSON
      }
    };
    ws.onclose = () => {
      setConnected(false);
      // Backoff reconnect
      retriesRef.current += 1;
      const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(retriesRef.current, 5)));
      reconnectRef.current = setTimeout(connect, delay);
    };
    ws.onerror = () => {
      try { ws.close(); } catch {}
    };
  }, []);

  useEffect(() => {
    connect();
    // Heartbeat every 25s
    const ping = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === 1) {
        try { wsRef.current.send('ping'); } catch {}
      }
    }, 25000);
    return () => {
      clearInterval(ping);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        try { wsRef.current.close(); } catch {}
      }
    };
  }, [connect]);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      try { wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data)); } catch {}
    }
  }, []);

  return { connected, lastMessage, send };
}
